import { Table as TableIcon } from "lucide-react";
import { useState } from "react";

import { Popover, PopoverContent, PopoverTrigger, Tip } from "@/components/ui/overlays";
import { cn } from "@/lib/utils";

/** The grid the picker offers. Google Docs shows a 10×10 and grows it as you
 *  reach the edge; a note is not a spreadsheet, so a fixed grid this size covers
 *  every table anyone sensibly writes in prose and keeps the popover small. */
const MAX_ROWS = 8;
const MAX_COLS = 8;

/** "Insert table" — the Google Docs affordance: a grid of cells where the
 *  rectangle under the pointer previews the table you are about to get, and the
 *  size is read out underneath. Clicking commits it.
 *
 *  `rows` in the callback counts BODY rows, matching `insertTable`; the header
 *  row is implied, so the top row of the grid means "header only". */
export function TableSizePicker({ onPick }: { onPick: (rows: number, cols: number) => void }) {
  const [open, setOpen] = useState(false);
  // 1-based extent of the highlighted rectangle; 0 = nothing hovered yet.
  const [hover, setHover] = useState<{ r: number; c: number }>({ r: 0, c: 0 });

  const commit = (r: number, c: number) => {
    setOpen(false);
    setHover({ r: 0, c: 0 });
    // The first grid row is the header, so the body gets the remainder.
    onPick(Math.max(0, r - 1), c);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setHover({ r: 0, c: 0 });
      }}
    >
      <Tip label="Insert table">
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Insert table"
            className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <TableIcon className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
      </Tip>
      <PopoverContent align="end">
        <div className="p-2" onMouseLeave={() => setHover({ r: 0, c: 0 })}>
          <div
            className="grid gap-0.5"
            style={{ gridTemplateColumns: `repeat(${MAX_COLS}, 1rem)` }}
            role="grid"
            aria-label="Table size"
          >
            {Array.from({ length: MAX_ROWS }, (_, ri) =>
              Array.from({ length: MAX_COLS }, (_, ci) => {
                const r = ri + 1;
                const c = ci + 1;
                const on = r <= hover.r && c <= hover.c;
                return (
                  <button
                    key={`${r}:${c}`}
                    type="button"
                    tabIndex={-1}
                    aria-label={`${r} by ${c}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setHover({ r, c })}
                    onFocus={() => setHover({ r, c })}
                    onClick={() => commit(r, c)}
                    className={cn(
                      "h-4 w-4 rounded-[2px] border transition-colors",
                      on ? "border-primary bg-primary/40" : "border-border bg-muted/40",
                    )}
                  />
                );
              }),
            )}
          </div>
          <p className="pt-2 text-center font-mono text-[11px] text-muted-foreground">
            {hover.r > 0 ? `${hover.r} × ${hover.c}` : "Insert table"}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
